/**
 * Design System - Shadow Tokens
 * 
 * Centralized shadow system for depth, elevation, and visual hierarchy.
 * Includes box shadows, inset shadows, and elevation patterns.
 * 
 * Usage:
 *   import { shadows, elevation } from '../design-system/tokens/shadows';
 *   <div style={{ boxShadow: shadows.md }} />
 */

/**
 * Box Shadow Scale
 * 
 * Progressive shadow scale from subtle (sm) to dramatic (2xl).
 * Use shadows to create depth and visual hierarchy.
 * 
 * Shadow design:
 * - Two-layer shadows (ambient + directional) for realistic depth
 * - Neutral gray colors for universal compatibility
 * - Progressively stronger with size
 */
export const shadows = {
  /**
   * None - No shadow
   * Use for: Flat elements, disabled states
   */
  none: 'none',
  
  /**
   * Extra Small - Minimal depth
   * Use for: Subtle hover states, very light elevation
   */
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  
  /**
   * Small - Light depth
   * Use for: Buttons, small cards, dropdowns
   */
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  
  /**
   * Base - Standard depth (default)
   * Use for: Cards, panels, most elevated elements
   */
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  
  /**
   * Medium - Moderate depth
   * Use for: Raised cards, prominent panels
   */
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  
  /**
   * Large - Strong depth
   * Use for: Modals, popovers, floating elements
   */
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  
  /**
   * Extra Large - Very strong depth
   * Use for: Major modals, important overlays
   */
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  
  /**
   * 2X Large - Dramatic depth
   * Use for: Critical modals, full-page overlays
   */
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

/**
 * Inset Shadows
 * 
 * Shadows that create a depressed or inward effect.
 * Useful for inputs, wells, and recessed areas.
 * 
 * Note: The app already uses inset shadows in index.css for specific patterns.
 */
export const insetShadows = {
  /**
   * None - No inset shadow
   */
  none: 'none',
  
  /**
   * Small - Subtle inset
   * Use for: Input fields, text areas
   */
  sm: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  
  /**
   * Base - Standard inset
   * Use for: Pressed buttons, active inputs
   */
  base: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  
  /**
   * Medium - Deeper inset
   * Use for: Wells, deeply recessed areas
   */
  md: 'inset 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
};

/**
 * Colored Shadows
 * 
 * Shadows with brand colors for special emphasis.
 * Use sparingly for focused states or brand moments.
 */
export const coloredShadows = {
  /**
   * Primary (Blue) - Brand color shadow
   * Use for: Primary button focus, brand emphasis
   */
  primary: {
    sm: '0 0 0 3px rgba(37, 99, 235, 0.1)',         // Blue-600 at 10%
    md: '0 0 0 4px rgba(37, 99, 235, 0.2)',         // Blue-600 at 20%
    lg: '0 4px 12px rgba(37, 99, 235, 0.3)',        // Blue-600 at 30%
  },
  
  /**
   * Accent (Green) - Success color shadow
   * Use for: Success button focus, positive actions
   */
  accent: {
    sm: '0 0 0 3px rgba(74, 222, 128, 0.1)',        // Green-400 at 10%
    md: '0 0 0 4px rgba(74, 222, 128, 0.2)',        // Green-400 at 20%
    lg: '0 4px 12px rgba(74, 222, 128, 0.3)',       // Green-400 at 30%
  },
  
  /**
   * Error (Red) - Error color shadow
   * Use for: Error state focus, destructive actions
   */
  error: {
    sm: '0 0 0 3px rgba(239, 68, 68, 0.1)',         // Red-500 at 10%
    md: '0 0 0 4px rgba(239, 68, 68, 0.2)',         // Red-500 at 20%
    lg: '0 4px 12px rgba(239, 68, 68, 0.3)',        // Red-500 at 30%
  },
};

/**
 * Focus Ring Shadows
 * 
 * Specialized shadows for focus states.
 * Ensure accessibility by always showing focus indicators.
 */
export const focusRings = {
  /**
   * Default focus ring
   */
  default: '0 0 0 3px rgba(37, 99, 235, 0.5)',      // Blue-600 at 50%
  
  /**
   * Thin focus ring
   */
  thin: '0 0 0 2px rgba(37, 99, 235, 0.5)',         // Blue-600 at 50%
  
  /**
   * Thick focus ring
   */
  thick: '0 0 0 4px rgba(37, 99, 235, 0.5)',        // Blue-600 at 50%
  
  /**
   * Success focus ring
   */
  success: '0 0 0 3px rgba(74, 222, 128, 0.5)',     // Green-400 at 50%
  
  /**
   * Error focus ring
   */
  error: '0 0 0 3px rgba(239, 68, 68, 0.5)',        // Red-500 at 50%
};

/**
 * Elevation System
 * 
 * Semantic elevation levels that combine z-index and shadows.
 * Use these to create consistent layering throughout the app.
 * 
 * Each level includes:
 * - shadow: The box-shadow to apply
 * - zIndex: The z-index value (references zIndexScale.js)
 * - description: When to use this elevation
 */
export const elevation = {
  /**
   * Level 0 - Flat
   * No shadow, base z-index
   */
  flat: {
    shadow: shadows.none,
    zIndex: 0,
    description: 'Base content, no elevation',
  },
  
  /**
   * Level 1 - Raised
   * Subtle shadow, low z-index
   */
  raised: {
    shadow: shadows.sm,
    zIndex: 10,
    description: 'Slightly elevated cards, buttons',
  },
  
  /**
   * Level 2 - Floating
   * Moderate shadow, medium z-index
   */
  floating: {
    shadow: shadows.md,
    zIndex: 100,
    description: 'Dropdowns, tooltips, floating elements',
  },
  
  /**
   * Level 3 - Overlay
   * Strong shadow, high z-index
   */
  overlay: {
    shadow: shadows.lg,
    zIndex: 1000,
    description: 'Popovers, menus, overlays',
  },
  
  /**
   * Level 4 - Modal
   * Very strong shadow, very high z-index
   */
  modal: {
    shadow: shadows.xl,
    zIndex: 5100,
    description: 'Modals, dialogs',
  },
  
  /**
   * Level 5 - Critical
   * Dramatic shadow, highest z-index
   */
  critical: {
    shadow: shadows['2xl'],
    zIndex: 10000,
    description: 'Critical alerts, toasts',
  },
};

/**
 * Shadow Patterns
 * 
 * Pre-configured shadow combinations for common UI patterns.
 */
export const shadowPatterns = {
  // Component shadows
  button: shadows.sm,
  buttonHover: shadows.md,
  card: shadows.base,
  cardHover: shadows.lg,
  input: insetShadows.sm,
  inputFocus: focusRings.default,
  
  // Layout shadows
  navbar: shadows.sm,
  sidebar: shadows.md,
  dropdown: shadows.lg,
  modal: shadows.xl,
  tooltip: shadows.md,
  
  // Interactive shadows
  hover: shadows.md,
  active: insetShadows.base,
  focus: focusRings.default,
  disabled: shadows.none,
};

/**
 * Tailwind Class Helpers
 * 
 * Pre-composed Tailwind shadow classes for quick use.
 */
export const tailwindClasses = {
  none: 'shadow-none',
  xs: 'shadow-xs',
  sm: 'shadow-sm',
  base: 'shadow',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  '2xl': 'shadow-2xl',
  inner: 'shadow-inner',
};

/**
 * Consolidated shadows export
 */
export const shadowTokens = {
  shadows,
  inset: insetShadows,
  colored: coloredShadows,
  focus: focusRings,
  elevation,
  patterns: shadowPatterns,
  tailwind: tailwindClasses,
};

/**
 * Default export
 */
export default shadowTokens;
